import { useEffect, useState } from 'react'
import axios from 'axios'
import { AlertTriangle, Image as ImageIcon, Loader2, Wallet } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { useLanguage } from '../contexts/LanguageContext'
import { currencySymbol, formatMoney, getApiUrl } from '../lib/utils'
import { formatBytes } from '../lib/media'

const API = getApiUrl()

type Transfer = { currency: string; amount: number }
type MediaSummary = { total: number; photos: number; videos: number; bytes: number }

type Props = {
  eventId: string | null
  eventTitle?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a successful delete, so the caller can refresh or navigate. */
  onDeleted?: (transferred: Transfer[]) => void
}

/**
 * Two-step confirmation for deleting an event.
 *
 * Step 1 is the event itself: it is irreversible, and it moves money —
 * whatever the event raised is credited to the host's account balance on the
 * way out. The dialog asks the server for that figure first and states it
 * plainly; nobody should have to guess what happens to their guests' gifts.
 *
 * Step 2 exists because the guest album goes too and R2 holds the ONLY copy of
 * those photos and videos. That is the loss a host is least likely to have
 * thought about and the one nothing can undo, so it gets its own screen with
 * its own count rather than a clause in a paragraph. Skipped entirely when the
 * album is empty — a second click that says "0 photos" only teaches people to
 * click through warnings.
 */
const DeleteEventDialog = ({
  eventId,
  eventTitle,
  open,
  onOpenChange,
  onDeleted,
}: Props) => {
  const { t, language } = useLanguage()
  const tr = (en: string, ka: string) => (language === 'ka' ? ka : en)

  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [guests, setGuests] = useState(0)
  const [media, setMedia] = useState<MediaSummary | null>(null)
  const [step, setStep] = useState<1 | 2>(1)
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!open || !eventId) return
    let active = true
    const load = async () => {
      setLoading(true)
      setTransfers([])
      setStep(1)
      try {
        const { data } = await axios.get(
          `${API}/events/${eventId}/deletion-preview`,
          { withCredentials: true },
        )
        if (!active) return
        setTransfers(data.transfers || [])
        setGuests(data.guests || 0)
        setMedia(data.media || null)
      } catch {
        // The preview is informational; failing to load it must not stop the
        // host from deleting. The delete itself still moves the money and
        // purges the album either way.
        if (active) {
          setTransfers([])
          setMedia(null)
        }
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [open, eventId])

  const handleDelete = async () => {
    if (!eventId) return
    setDeleting(true)
    try {
      const { data } = await axios.delete(`${API}/events/${eventId}`, {
        withCredentials: true,
      })
      const moved: Transfer[] = data?.transferred || []
      if (moved.length > 0) {
        toast.success(
          t.eventDeletedWithFunds.replace(
            '{amount}',
            moved
              .map((m) => `${currencySymbol(m.currency)}${formatMoney(m.amount)}`)
              .join(', '),
          ),
        )
      } else {
        toast.success(tr('Event deleted', 'ღონისძიება წაიშალა'))
      }
      onOpenChange(false)
      onDeleted?.(moved)
    } catch {
      toast.error(tr('Failed to delete event', 'ვერ წაიშალა'))
    } finally {
      setDeleting(false)
    }
  }

  const hasMedia = (media?.total || 0) > 0
  // Only ask twice when there is actually an album to lose.
  const needsMediaStep = hasMedia && step === 1
  const mediaCountLabel = () => {
    if (!media) return ''
    const parts: string[] = []
    if (media.photos)
      parts.push(
        language === 'ka'
          ? `${media.photos} ფოტო`
          : `${media.photos} photo${media.photos === 1 ? '' : 's'}`,
      )
    if (media.videos)
      parts.push(
        language === 'ka'
          ? `${media.videos} ვიდეო`
          : `${media.videos} video${media.videos === 1 ? '' : 's'}`,
      )
    return parts.join(language === 'ka' ? ' და ' : ' and ')
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-event-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 font-display">
            <AlertTriangle
              className={`h-5 w-5 shrink-0 ${step === 2 ? 'text-amber-500' : 'text-red-600'}`}
            />
            {step === 1 ? t.deleteEventTitle : t.deleteMediaTitle}
            {hasMedia && (
              <span className="ml-auto shrink-0 text-[11px] font-medium tabular-nums text-[#4a3d66] dark:text-[#c3b8dd]">
                {step}/2
              </span>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {step === 1 ? (
              <>
                {eventTitle ? `“${eventTitle}” — ` : ''}
                {t.deleteEventBody}
                {guests > 0 && (
                  <span className="mt-1 block">
                    {tr(
                      `${guests} guest${guests === 1 ? '' : 's'} will be removed with it.`,
                      `წაიშლება ${guests} სტუმარიც.`,
                    )}
                  </span>
                )}
              </>
            ) : (
              t.deleteMediaBody
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-[#ff4d8d]" />
          </div>
        ) : step === 1 ? (
          transfers.length > 0 && (
            <div
              className="rounded-xl border border-[#ff4d8d]/25 bg-[#ff4d8d]/[0.06] p-3"
              data-testid="delete-event-transfers"
            >
              <p className="flex items-center gap-2 text-[13px] font-medium text-[#1b1033] dark:text-[#f4f0ff]">
                <Wallet className="h-4 w-4 shrink-0 text-[#ff4d8d]" />
                {t.deleteEventFunds}
              </p>
              <ul className="mt-2 space-y-1">
                {transfers.map((tx) => (
                  <li
                    key={tx.currency}
                    className="flex items-center justify-between text-sm tabular-nums"
                  >
                    <span className="text-[#4a3d66] dark:text-[#c3b8dd]">
                      {tx.currency}
                    </span>
                    <span className="font-semibold text-[#ff4d8d]">
                      {currencySymbol(tx.currency)}
                      {formatMoney(tx.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )
        ) : (
          <div
            className="rounded-xl border border-amber-500/30 bg-amber-500/[0.08] p-3"
            data-testid="delete-event-media"
          >
            <p className="flex items-center gap-2 text-[13px] font-medium text-[#1b1033] dark:text-[#f4f0ff]">
              <ImageIcon className="h-4 w-4 shrink-0 text-amber-600" />
              {mediaCountLabel()}
              <span className="ml-auto tabular-nums text-[#4a3d66] dark:text-[#c3b8dd]">
                {formatBytes(media?.bytes || 0)}
              </span>
            </p>
            <p className="mt-1.5 text-[13px] text-[#4a3d66] dark:text-[#c3b8dd]">
              {t.deleteMediaNoCopy}
            </p>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={deleting}
            className="rounded-xl"
            onClick={(e) => {
              // On step 2 this is "Back", so it must not dismiss the dialog.
              if (step === 2) {
                e.preventDefault()
                setStep(1)
              }
            }}
          >
            {step === 1 ? tr('Cancel', 'გაუქმება') : tr('Back', 'უკან')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Keep the dialog up on both steps; Radix would close it on click
              // and the host would never see step 2, or a failure.
              e.preventDefault()
              if (needsMediaStep) setStep(2)
              else handleDelete()
            }}
            disabled={deleting || loading}
            className="rounded-xl bg-red-600 text-white hover:bg-red-700"
            data-testid={needsMediaStep ? 'delete-event-continue' : 'delete-event-confirm'}
          >
            {deleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : needsMediaStep ? (
              tr('Continue', 'გაგრძელება')
            ) : (
              t.deleteEventConfirm
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteEventDialog
